import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { setEvents, setLoading, setError, updateEventError } from '../store/eventsSlice';
import type { PricingEvent, PricingEventError } from '../store/eventsSlice';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';

export const useEvents = (enabled: boolean = true) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const companies = useAppSelector(state => state.supaPages.pages);
  const events = useAppSelector(state => state.events.events);
  const loading = useAppSelector(state => state.events.loading);
  const error = useAppSelector(state => state.events.error);

  // Check if we're on a company details page
  const isCompanyDetailsPage = location.pathname.startsWith('/companies/');

  const logError = async (
    slug: string,
    v1: string,
    v2: string,
    screenshotError: boolean,
    dataError: boolean,
    errorDesc: string
  ) => {
    try {


      const errorData: PricingEventError = {
        screenshotError,
        dataError,
        errorDesc,
        timestamp: Date.now()
      };

      const { data, error } = await supabase.functions.invoke('update-analysis', {
        body: {
          slug,
          v1,
          v2,
          json: {
            error: errorData
          }
        }
      });

      if (error) {

        toast.error('Failed to log error');
        return;
      }

      dispatch(updateEventError({ slug, v1, v2, error: errorData }));


      toast.success('Error logged successfully');
    } catch (error) {

      toast.error('Failed to log error');
    }
  };

  const fetchEvents = async (quarters: string[] = [], changeTypes: string[] = []) => {
    try {
      // Skip loading events if we're on a company details page
      if (isCompanyDetailsPage) {

        return events;
      }

      if (events.length > 0 || !enabled) {

        return events;
      }

      dispatch(setLoading(true));
      console.log('🔄 [useEvents] Fetching events...');

      const quartersToUse = quarters.includes('All') ? [] : quarters;
      const changeTypesToUse = changeTypes.includes('All') ? [] : changeTypes;

      const { data, error } = await supabase.functions.invoke('query_events', {
        body: {
          quarters: quartersToUse,
          changeTypes: changeTypesToUse
        }
      });

      if (error) {
        console.error('❌ [useEvents] Error fetching events:', error);
        dispatch(setError(error.message));
        return [];
      }

      // Augment events with company data
      const augmentedEvents = (data.events || []).map((event: PricingEvent) => {
        const company = companies.find(c => c.slug === event.slug);
        if (!company) {
          // Instead of logging a warning for each missing company, just count them
          // We'll log a summary at the end
          return {
            ...event,
            logoUrl: '',
            category: '',
            description: event.changes?.join(' • ') || '',
            employees: '',
            pricingPageUrl: '',
            screenshotUrl: '',
            screenshotUrlVersion: '',
          };
        }

        // console.log(`✅ [useEvents] Augmenting event with company data for: ${event.slug}`);
        return {
          ...event,
          logoUrl: company.logoUrl,
          category: company.category,
          description: event.changes?.join(' • ') || company.description || '',
          employees: company.employees,
          pricingPageUrl: company.pricingPageUrl,
          screenshotUrl: company.screenshotUrl,
          screenshotUrlVersion: company.screenshotUrlVersion,
        };
      });

      // Count and log missing companies once instead of for each event
      const missingCompanies = new Set();
      augmentedEvents.forEach(event => {
        if (!companies.find(c => c.slug === event.slug)) {
          missingCompanies.add(event.slug);
        }
      });

      if (missingCompanies.size > 0) {
        console.log(`⚠️ [useEvents] Missing company data for ${missingCompanies.size} companies`);
      }

      console.log('✅ [useEvents] Events fetched and augmented:', augmentedEvents.length);
      dispatch(setEvents(augmentedEvents));
      return augmentedEvents;
    } catch (error) {
      console.error('❌ [useEvents] Error:', error);
      dispatch(setError(error.message));
      return [];
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Remove automatic loading - events will be loaded manually when needed
  // useEffect(() => {
  //   if (enabled && events.length === 0 && !isCompanyDetailsPage) {
  //     console.log('🔄 [useEvents] Loading events (not on company details page)');
  //     fetchEvents();
  //   }
  // }, [enabled, events.length, isCompanyDetailsPage]);

  return {
    events,
    loading,
    error,
    fetchEvents,
    logError
  };
};